import {
  Box,
  Button,
  Grid2,
  Typography,
  useTheme,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useStyles } from "./styles";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { createRef, useState, useEffect } from "react";
import constants from "constants/constants.json";
import Notification from "components/snackbar";
import { getToken } from "utils/loadAuth";
import { useAuth0 } from "@auth0/auth0-react";
import {
  getPermissionById,
  createExternalPermission,
  updateExternalPermission,
} from "pages/userManagement/permissions/permission.service";

import { fetchExternalRoles } from "pages/userManagement/roles/roles.services";

const PermissionsSettings = ({ isNavbarOpen }) => {
  const theme = useTheme();
  const classes = useStyles({ theme, isNavbarOpen });
  const navigate = useNavigate();

  const [selectedRole, setSelectedRole] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [permissionScope, setPermissionScope] = useState("");
  const [permissionDescription, setPermissionDescription] = useState("");
  const [roles, setRoles] = useState([]);

  const scrollRef = createRef();

  const [openNotification, setOpenNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const handleSave = async (permission) => {
    try {
      setLoading(true);
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });

      let permissionResponse;

      const isEdit = Boolean(location.state?.permissionId);

      if (isEdit) {
        permissionResponse = await updateExternalPermission({
          token,
          permission: { ...permission, id: location.state.permissionId },
        });
      } else {
        permissionResponse = await createExternalPermission({
          token,
          permission,
        });
      }
      if (!permissionResponse) {
        setOpenNotification(true);
        setMessage(`Failed to ${isEdit ? "update" : "create"} permission`);
        setSeverity("error");
        return;
      }
      setOpenNotification(true);
      setMessage(`Permission ${isEdit ? "updated" : "created"} successfully`);
      setSeverity("success");
    } catch (error) {
      console.error("Error creating permission:", error);
      setOpenNotification(true);
      setMessage("Failed to create permission");
      setSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getRoles = async (token) => {
      try {
        const rolesResponse = await fetchExternalRoles({ token });
        return rolesResponse;
      } catch (error) {
        console.error("Error fetching roles:", error);
        return [];
      }
    };
    const fetchPermissionDetails = async () => {
      try {
        setLoading(true);
        const token = await getToken({
          getAccessTokenSilently,
          loginWithRedirect,
        });
        const rolesData = await getRoles(token);
        setRoles(rolesData);

        if (!location.state?.permissionId) {
          return;
        }
        const { permission, assignedRoles } = await getPermissionById({
          token,
          permissionId: location.state.permissionId,
        });

        setPermissionScope(permission?.scope || "");
        setPermissionDescription(permission?.scopeDesc || "");
        setSelectedRole(assignedRoles.map((r) => r.roleName));
      } catch (error) {
        console.error("Error fetching permission details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissionDetails();
  }, [location.state, getAccessTokenSilently, loginWithRedirect]);

  return (
    <Grid2 sx={{ minHeight: "700px", backgroundColor: theme.palette.bgColor }}>
      <Grid2 size={10} className={classes.wrapper} ref={scrollRef}>
        <Box className={classes.viewContainer}>
          <Box className={classes.titleWrapper}>
            <Typography
              data-testid="popupTitle"
              className={classes.title}
              variant="h5"
              align="center"
            >
              {permissionScope
                ? "Update Permission Details"
                : "Create New Permission"}
            </Typography>
            <Box className={classes.actionWrapper}>
              <Button
                className={classes.saveBtn}
                data-testid="save-role-btn"
                disabled={loading || !permissionScope || !permissionDescription}
                onClick={() =>
                  handleSave({
                    scope: permissionScope,
                    scopeDesc: permissionDescription,
                    roleNames: selectedRole,
                  })
                }
              >
                {constants.save}
              </Button>
              <Button
                className={classes.back}
                data-testid="back-btn"
                onClick={() => {
                  if (location.state?.backTo) {
                    navigate(location.state?.backTo, {
                      state: {
                        activeTab: "roles",
                        roleId: location.state?.roleId || null,
                      },
                    });
                    return;
                  }
                  navigate("/usermanagement/permissions");
                }}
              >
                {constants.back}
              </Button>
            </Box>
          </Box>
          <Grid2 container className={classes.pageWrapper}>
            <Typography
              data-testid="transfer-list"
              className={clsx(classes.text, classes.titles)}
              variant="subtitle1"
            >
              {constants.permissionDetails}
            </Typography>
            <Grid2
              container
              justifyContent={"space-between"}
              flex={1}
              flexDirection={"row"}
              sx={{ marginBottom: "1rem" }}
            >
              <Grid2
                className={classes.inputWrapper}
                justifyContent={"space-between"}
                display={"flex"}
                flexDirection={"column"}
                gap={2}
              >
                <TextField
                  disabled={loading}
                  slotProps={{
                    inputLabel: {
                      style: { color: theme.palette.text.primary },
                      shrink: false,
                    },
                  }}
                  placeholder={constants.permissionsScope}
                  variant="outlined"
                  value={permissionScope}
                  id="permission-scope-input"
                  onChange={(e) => setPermissionScope(e.target.value)}
                  data-testid="permission-scope-input"
                  className={classes.inputText}
                />

                <Autocomplete
                  disabled={loading}
                  multiple
                  getOptionDisabled={(option) => {
                    const isDisabled = roles.find(
                      (role) => role.roleName === option
                    )?.disabled;
                    return isDisabled || false;
                  }}
                  loading={loading}
                  disableCloseOnSelect
                  id="roles-autocomplete"
                  value={selectedRole}
                  options={roles
                    .map((r) => r.roleName)
                    .filter((role) => !selectedRole.includes(role))}
                  getOptionLabel={(option) => option}
                  filterSelectedOptions
                  onChange={(_, newValue) => {
                    setSelectedRole(newValue.map((role) => role));
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        backgroundColor: theme.palette.themeColor,
                        color: theme.palette.text.primary,
                      },
                    },
                    listbox: {
                      sx: {
                        "& .MuiAutocomplete-option": {
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "light"
                                ? "#d1d1d1ff"
                                : "rgba(255, 255, 255, 0.16)",
                          },
                        },
                        maxHeight: 250,
                        overflowY: "auto",
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      slotProps={{
                        inputLabel: {
                          style: { color: theme.palette.text.primary },
                          shrink: false,
                        },
                      }}
                      placeholder={
                        selectedRole.length === 0 ? constants.selectRoles : ""
                      }
                      className={classes.inputText}
                      {...params}
                    />
                  )}
                />
              </Grid2>
              <Grid2 className={classes.inputWrapper}>
                <TextField
                  disabled={loading}
                  slotProps={{
                    inputLabel: {
                      style: { color: theme.palette.text.primary },
                      shrink: false,
                    },
                  }}
                  className={classes.inputText}
                  multiline
                  fullWidth
                  maxRows={4}
                  minRows={4}
                  id="permission-description-input"
                  placeholder={constants.permissionDesc}
                  variant="outlined"
                  value={permissionDescription}
                  onChange={(e) => setPermissionDescription(e.target.value)}
                  data-testid="permission-description-input"
                />
              </Grid2>
            </Grid2>
          </Grid2>
          <Notification
            message={message}
            open={openNotification}
            setOpen={setOpenNotification}
            severity={severity}
          />
        </Box>
      </Grid2>
    </Grid2>
  );
};

PermissionsSettings.propTypes = {
  permission: PropTypes.any,
  isNavbarOpen: PropTypes.bool,
};
export default PermissionsSettings;
